import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/apiClient';
import Modal from '../ui/Modal';
import Breadcrumb from '../ui/Breadcrumb';
import { IconEdit, IconPlus, IconShield, IconUsers } from '../ui/icons';
import PlaceholderPage from './PlaceholderPage';
import { moduleColor } from '../ui/modulePalette';

function roleLabel(role) {
  if (role === 'owner') return 'Owner';
  if (role === 'manager') return 'Manager';
  if (role === 'editor') return 'Editor';
  return 'Viewer';
}

function roleSubtitle(role) {
  if (role === 'owner') return 'Accès complet (responsable principal).';
  if (role === 'manager') return 'Gestion du site et des membres.';
  if (role === 'editor') return 'Accès modules (sans gestion équipe).';
  return 'Lecture seule (dashboard).';
}

export default function TeamPage({ siteId, site, user, modules }) {
  const canAccess = useMemo(() => (modules || []).some((m) => m.key === 'team' && !m.coming_soon), [modules]);
  const isAdmin = user?.role === 'admin';

  const [state, setState] = useState({
    status: 'loading',
    members: [],
    availableModules: [],
    rolePermissions: {},
    error: '',
  });
  const [modal, setModal] = useState(null);

  const moduleByKey = useMemo(() => {
    const map = new Map();
    for (const m of state.availableModules || []) {
      if (!m || !m.key) continue;
      map.set(String(m.key), m);
    }
    return map;
  }, [state.availableModules]);

  const enabledModuleKeys = useMemo(() => (state.availableModules || []).map((m) => m.key), [state.availableModules]);

  const refresh = useCallback(() => {
    if (!siteId) return () => {};
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    api
      .members(Number(siteId))
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'error', members: [], availableModules: [], rolePermissions: {}, error: res.error || 'Impossible de charger' });
          return;
        }
        setState({
          status: 'ready',
          members: res.members || [],
          availableModules: res.available_modules || [],
          rolePermissions: res.role_permissions || {},
          error: '',
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', members: [], availableModules: [], rolePermissions: {}, error: 'Impossible de charger' });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  useEffect(() => refresh(), [refresh]);

  const sortedMembers = useMemo(() => {
    const list = state.members || [];
    return [...list].sort((a, b) => (a.display_name || a.email).localeCompare(b.display_name || b.email, 'fr'));
  }, [state.members]);

  function moduleMeta(moduleKey) {
    const k = String(moduleKey || '');
    const m = moduleByKey.get(k) || null;
    const fallbackLabels = {
      appointments: 'Rendez-vous',
      updates: 'Mises à jour',
      settings: 'Paramètres du site',
      backups: 'Sauvegarde',
      projects: 'Projets',
      resources: 'Ressources',
      databases: 'Bases de données',
      languages: 'Langues',
      team: 'Équipe',
      dashboard: 'Dashboard',
      articles: 'Articles',
      traffic: 'Trafic',
      scripts: 'Scripts',
    };
    const rawLabel = String(m?.label || '').trim();
    const label = rawLabel && rawLabel !== k ? rawLabel : (fallbackLabels[k] || k);
    return {
      key: k,
      label,
      color: String(m?.color || '') || moduleColor(k, ''),
      comingSoon: Boolean(m?.coming_soon),
    };
  }

  function MemberModulesModal({ member }) {
    const name = member?.display_name || member?.email || 'Membre';
    const keys = Array.isArray(member?.modules) ? member.modules : [];
    const list = keys.map(moduleMeta).filter((x) => x.key);
    return (
      <Modal
        title={`Modules — ${name}`}
        icon={<IconShield />}
        onClose={() => setModal(null)}
        footer={
          <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
            Fermer
          </button>
        }
      >
        {list.length ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {list.map((m) => (
              <div key={m.key} className="ButtonInline" style={{ gap: 10, justifyContent: 'flex-start' }}>
                <span className="ColorDot" aria-hidden="true" style={m.color ? { '--accent': m.color } : undefined} />
                <span>{m.label}</span>
                {m.comingSoon ? <span className="Badge">à venir</span> : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="Muted">Aucun module.</div>
        )}
      </Modal>
    );
  }

  const roles = useMemo(() => (isAdmin ? ['owner', 'manager', 'editor', 'viewer'] : ['manager', 'editor', 'viewer']), [isAdmin]);

  function ensureDashboard(keys) {
    const next = new Set(keys || []);
    next.add('dashboard');
    return Array.from(next);
  }

  function openAddMember() {
    const defaultRole = 'viewer';
    const defaults = state.rolePermissions?.[defaultRole] || (defaultRole === 'viewer' ? ['dashboard'] : enabledModuleKeys);
    setModal({
      type: 'add-member',
      email: '',
      display_name: '',
      member_role: defaultRole,
      modules: ensureDashboard(defaults),
      saving: false,
      error: '',
      tempPassword: '',
    });
  }

  function openEditMember(m) {
    setModal({
      type: 'edit-member',
      membership_id: m.membership_id,
      email: m.email,
      display_name: m.display_name || '',
      member_role: m.member_role,
      modules: ensureDashboard(m.modules || []),
      saving: false,
      error: '',
    });
  }

  async function saveRolePermissions(role, moduleKeys) {
    const res = await api.updateRolePermissions({ site_id: Number(siteId), member_role: role, modules: moduleKeys });
    if (!res.ok) return res;
    refresh();
    return res;
  }

  if (!canAccess) {
    return <PlaceholderPage title="Équipe" subtitle="Accès refusé ou module indisponible." />;
  }

	  return (
	    <div className="Page">
	      <header className="PageHeader">
	        <div className="PageHeaderBar">
	          <Breadcrumb items={[{ label: 'Équipe' }]} />
	          <div className="Badges">
	            <button type="button" className="Button Button--ghost" onClick={() => setModal({ type: 'role-permissions' })}>
	              <span className="ButtonInline">
	                <IconShield /> Permissions rôles
	              </span>
            </button>
            <button type="button" className="Button" onClick={openAddMember}>
              <span className="ButtonInline">
                <IconPlus /> Ajouter un membre
	              </span>
	            </button>
	          </div>
	        </div>
	      </header>

      {state.status === 'error' ? (
        <div className="Card">
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      <section className="Card" aria-label="Membres">
        <div className="CardHeader">
          <div className="TileTitle">Membres</div>
        </div>

        {state.status === 'loading' ? <div className="Muted">Chargement…</div> : null}
        {state.status === 'ready' && sortedMembers.length === 0 ? <div className="Muted">Aucun membre.</div> : null}

        {state.status === 'ready' ? (
          <div className="List" role="list">
            {sortedMembers.map((m) => (
              <div className="ListItem" role="listitem" key={m.membership_id}>
                <div>
                  <div className="TeamMemberName">
                    <span className="ButtonInline" style={{ gap: 8, justifyContent: 'flex-start' }}>
                      <span>{m.display_name || m.email}</span>
                      <span className="Badge">{roleLabel(m.member_role)}</span>
                    </span>
                  </div>
                  <div className="Muted">
                    {m.email}
                  </div>
                  <div className="Badges" style={{ marginTop: 10, justifyContent: 'flex-start' }}>
                    {(Array.isArray(m.modules) ? m.modules : [])
                      .slice(0, 3)
                      .map((k) => moduleMeta(k))
                      .map((mm) => (
                        <span key={mm.key} className="Badge">
                          <span className="ButtonInline" style={{ gap: 8 }}>
                            <span className="ColorDot" aria-hidden="true" style={mm.color ? { '--accent': mm.color } : undefined} />
                            <span>{mm.label}</span>
                          </span>
                        </span>
                      ))}
                    {(Array.isArray(m.modules) ? m.modules : []).length > 3 ? (
                      <button type="button" className="BadgeButton" onClick={() => setModal({ type: 'member-modules', member: m })} title="Voir tous les modules">
                        +…
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="Badges">
                  <button type="button" className="Button Button--ghost" onClick={() => openEditMember(m)}>
                    <span className="ButtonInline">
                      <IconEdit /> Modifier
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      {modal?.type === 'role-permissions' ? (
        <Modal
          title="Permissions par rôle"
          icon={<IconShield />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Fermer
              </button>
            </>
          }
        >
          {roles.map((r) => (
            <RolePermissions
              key={r}
              role={r}
              availableModules={state.availableModules}
              value={state.rolePermissions?.[r] || []}
              disabled={state.status !== 'ready'}
              onSave={(keys) => saveRolePermissions(r, keys)}
            />
          ))}
        </Modal>
      ) : null}

      {modal?.type === 'add-member' ? (
        <Modal
          title="Ajouter un membre"
          icon={<IconUsers />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: '', tempPassword: '' }));
                  const res = await api.createMember({
                    site_id: Number(siteId),
                    email: modal.email,
                    display_name: modal.display_name,
                    member_role: modal.member_role,
                    modules: modal.modules,
                  });
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Création impossible' }));
                    return;
                  }
                  setModal((m) => ({ ...m, saving: false, error: '', tempPassword: res.temp_password || '' }));
                  refresh();
                }}
              >
                {modal.saving ? 'Création…' : 'Créer'}
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label" htmlFor="tm-email">
                Email
              </label>
              <input
                id="tm-email"
                className="Input"
                type="email"
                value={modal.email}
                onChange={(e) => setModal((m) => ({ ...m, email: e.target.value }))}
              />
            </div>
            <div className="Field">
              <label className="Label" htmlFor="tm-name">
                Nom (optionnel)
              </label>
              <input
                id="tm-name"
                className="Input"
                value={modal.display_name}
                onChange={(e) => setModal((m) => ({ ...m, display_name: e.target.value }))}
              />
            </div>
          </div>

          <div className="Field">
            <label className="Label" htmlFor="tm-role">
              Rôle
            </label>
            <select
              id="tm-role"
              className="Select"
              value={modal.member_role}
              onChange={(e) => {
                const role = e.target.value;
                const defaults = state.rolePermissions?.[role] || (role === 'viewer' ? ['dashboard'] : enabledModuleKeys);
                setModal((m) => ({ ...m, member_role: role, modules: ensureDashboard(defaults) }));
              }}
            >
              {isAdmin ? <option value="owner">owner</option> : null}
              <option value="manager">manager</option>
              <option value="editor">editor</option>
              <option value="viewer">viewer</option>
            </select>
          </div>

          <ModulePicker
            title="Accès modules"
            availableModules={state.availableModules}
            value={modal.modules}
            onChange={(modules) => setModal((m) => ({ ...m, modules }))}
            disabled={state.status !== 'ready'}
          />

          {modal.tempPassword ? (
            <div className="Notice">
              Mot de passe temporaire: <code>{modal.tempPassword}</code>
            </div>
          ) : null}

          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'edit-member' ? (
        <Modal
          title="Modifier un membre"
          icon={<IconUsers />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await api.updateMembership({
                    site_id: Number(siteId),
                    membership_id: modal.membership_id,
                    member_role: modal.member_role,
                    modules: modal.modules,
                  });
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Enregistrement impossible' }));
                    return;
                  }
                  setModal(null);
                  refresh();
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="Muted" style={{ marginBottom: 4 }}>
            {modal.display_name || modal.email}
          </div>
          <div className="Field">
            <label className="Label" htmlFor="tm-role-edit">
              Rôle
            </label>
            <select
              id="tm-role-edit"
              className="Select"
              value={modal.member_role}
              onChange={(e) => {
                const role = e.target.value;
                const defaults = state.rolePermissions?.[role] || (role === 'viewer' ? ['dashboard'] : enabledModuleKeys);
                setModal((m) => ({ ...m, member_role: role, modules: ensureDashboard(defaults) }));
              }}
            >
              {isAdmin ? <option value="owner">owner</option> : null}
              <option value="manager">manager</option>
              <option value="editor">editor</option>
              <option value="viewer">viewer</option>
            </select>
          </div>

          <ModulePicker
            title="Accès modules (spécifique)"
            availableModules={state.availableModules}
            value={modal.modules}
            onChange={(modules) => setModal((m) => ({ ...m, modules }))}
            disabled={state.status !== 'ready'}
          />

          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'member-modules' ? <MemberModulesModal member={modal.member} /> : null}
    </div>
  );
}

function RolePermissions({ role, availableModules, value, disabled, onSave }) {
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => setDraft(Array.from(new Set([...(value || []), 'dashboard']))), [value]);

  return (
    <div className="RoleBox">
      <div className="Row" style={{ marginBottom: 10 }}>
        <div>
          <div className="UserName">
            <span className="Badge">{roleLabel(role)}</span>
          </div>
          <div className="Muted">{roleSubtitle(role)}</div>
        </div>
        <button
          type="button"
          className="Button Button--ghost"
          disabled={disabled || status === 'saving'}
          onClick={async () => {
            setStatus('saving');
            setError('');
            const res = await onSave(draft);
            if (!res.ok) {
              setStatus('idle');
              setError(res.error || 'Erreur');
              return;
            }
            setStatus('idle');
          }}
        >
          <span className="ButtonInline">
            <IconShield /> {status === 'saving' ? 'Sauvegarde…' : 'Sauvegarder'}
          </span>
        </button>
      </div>

      <ModulePicker title={null} availableModules={availableModules} value={draft} onChange={setDraft} disabled={disabled} />
      {error ? (
        <div className="Error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function ModulePicker({ title, availableModules, value, onChange, disabled }) {
  const normalized = useMemo(() => {
    const next = new Set(value || []);
    next.add('dashboard');
    return Array.from(next);
  }, [value]);

  return (
    <div className="Field">
      {title ? <div className="Label">{title}</div> : null}
      <div className="CheckGrid" role="group" aria-label="Modules">
        {(availableModules || []).map((m) => (
          <label key={m.key} className="Check" style={m.color ? { '--accent': m.color } : undefined}>
            <input
              type="checkbox"
              checked={normalized.includes(m.key)}
              onChange={(e) => {
                const next = new Set(normalized);
                if (e.target.checked) next.add(m.key);
                else next.delete(m.key);
                onChange(Array.from(next));
              }}
              disabled={disabled || m.key === 'dashboard'}
            />
            <span className="CheckMain">
              <span className="ColorDot" aria-hidden="true" />
              <span style={{ minWidth: 0 }}>
                <span className="OneLine">{m.label}</span> {m.coming_soon ? <span className="Badge">à venir</span> : null}
              </span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
